import { z } from 'zod';
import { FirstName } from './character';
import { NeedEffects } from './play';

// Family part 2, as Lagos Life's 💞 Family (its rules read from the public client code on 21 and 23 October
// 2026): children and relatives. Two partners (dating, engaged or married) start a family: the one who asks
// names the baby, the other says yes, and the baby arrives two days later; or they adopt together and the
// child comes home straight away. Up to three children each. Feeding keeps a baby full for half a day and
// playing can be done every 20 minutes; both lift the parent a little, and a hungry baby is only shown as
// hungry. A parent can rename a child once a week, and the other parent has a day to say no. After a break-up
// both parents keep the children. Relatives (kin) are other players who agreed to be your sibling, cousin and
// so on. Decided with Jerry on 23 October 2026. All of it is content the admins edit.

const label = z.string().trim().min(2).max(40);
const emoji = z.string().trim().min(1).max(16);

// ---------- kin: what another player is to you ----------

export const KIN_KINDS = [
  'sibling',
  'cousin',
  'uncle',
  'nephew',
  'parent',
  'child',
  'grandparent',
  'grandchild',
  'inlaw',
] as const;
export type KinKind = (typeof KIN_KINDS)[number];
// what you are to them when they are this to you
export const KIN_INVERSE: Record<KinKind, KinKind> = {
  sibling: 'sibling',
  cousin: 'cousin',
  uncle: 'nephew',
  nephew: 'uncle',
  parent: 'child',
  child: 'parent',
  grandparent: 'grandchild',
  grandchild: 'grandparent',
  inlaw: 'inlaw',
};
export const KinLabel = z.object({ kind: z.enum(KIN_KINDS), label, emoji, visible: z.boolean() });
export type KinLabel = z.infer<typeof KinLabel>;

// ---------- babies ----------

export const BabyMilestone = z.object({
  // days since the baby arrived
  day: z.number().int().min(0).max(3_650),
  label: z.string().trim().min(2).max(60),
  emoji,
});
export type BabyMilestone = z.infer<typeof BabyMilestone>;

export const BabiesContent = z.object({
  visible: z.boolean(),
  // children each player can have (babies on the way and adoptions count)
  maxChildren: z.number().int().min(1).max(10),
  // a baby arrives this long after the yes; an adopted child comes home straight away
  arriveHours: z
    .number()
    .int()
    .min(0)
    .max(24 * 30),
  // a feed: what it costs, how long it keeps a baby full (care falls from 100 to 0 over that), and the care
  // below which the baby is hungry again (and can be fed)
  feedPence: z.number().int().min(0).max(10_000_000),
  fullHours: z.number().int().min(1).max(72),
  hungryBelow: z.number().int().min(1).max(100),
  // play: once every this many minutes
  playMinutes: z.number().int().min(1).max(1_440),
  // what feeding and playing do for the parent who does it
  feedEffects: NeedEffects,
  playEffects: NeedEffects,
  // a new name once every this many days; the other parent has this long to say no
  renameDays: z.number().int().min(0).max(365),
  renameAnswerHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  milestones: z.array(BabyMilestone).min(1).max(20),
});
export type BabiesContent = z.infer<typeof BabiesContent>;

const kin = (kind: KinKind, text: string, icon: string): KinLabel => ({
  kind,
  label: text,
  emoji: icon,
  visible: true,
});
export const DEFAULT_KIN: KinLabel[] = [
  kin('sibling', 'Sibling', '👫🏾'),
  kin('cousin', 'Cousin', '🤝🏾'),
  kin('uncle', 'Uncle / Aunty', '🧔🏾'),
  kin('nephew', 'Nephew / Niece', '🧒🏾'),
  kin('parent', 'Mum / Dad', '👨🏾‍👩🏾‍👧🏾'),
  kin('child', 'Son / Daughter', '👧🏾'),
  kin('grandparent', 'Grandma / Grandpa', '👵🏾'),
  kin('grandchild', 'Grandchild', '🧒🏾'),
  kin('inlaw', 'In-law', '🫱🏾‍🫲🏾'),
];
export const DEFAULT_BABIES: BabiesContent = {
  visible: true,
  maxChildren: 3,
  arriveHours: 48,
  feedPence: 2_500,
  fullHours: 12,
  hungryBelow: 50,
  playMinutes: 20,
  feedEffects: { social: 4, fun: 2 },
  playEffects: { fun: 6, social: 3 },
  renameDays: 7,
  renameAnswerHours: 24,
  milestones: [
    { day: 0, label: 'Newborn', emoji: '👶🏾' },
    { day: 1, label: 'First smile', emoji: '😊' },
    { day: 2, label: 'Crawling everywhere', emoji: '🐢' },
    { day: 4, label: 'First word: "Mama!"', emoji: '🗣️' },
    { day: 7, label: 'First steps', emoji: '🚶🏾' },
    { day: 14, label: 'Toddler on the loose', emoji: '🧒🏾' },
    { day: 30, label: 'Off to nursery', emoji: '🎒' },
  ],
};

export function childrenProblems(babies: BabiesContent, kinLabels: KinLabel[]): string[] {
  const problems: string[] = [];
  const days = babies.milestones.map((item) => item.day);
  if (days[0] !== 0) problems.push('Family: the first milestone is on day 0.');
  if (days.some((day, i) => i > 0 && day <= days[i - 1]!))
    problems.push('Family: milestones go in order, one per day at most.');
  const kinds = kinLabels.map((item) => item.kind);
  if (new Set(kinds).size !== kinds.length || kinds.length !== KIN_KINDS.length)
    problems.push('Family: each kind of relative appears once.');
  return problems;
}

// how full a baby is now: 100 just fed, down to 0 after `fullHours`
export const careLevel = (babies: BabiesContent, fedAt: Date | string | null, now: Date) =>
  fedAt
    ? Math.max(
        0,
        Math.min(
          100,
          Math.round(100 - ((now.getTime() - new Date(fedAt).getTime()) / (babies.fullHours * 3_600_000)) * 100),
        ),
      )
    : 0;
// when a baby fed then is hungry again
export const hungryAt = (babies: BabiesContent, fedAt: Date | string) =>
  new Date(new Date(fedAt).getTime() + ((100 - babies.hungryBelow) / 100) * babies.fullHours * 3_600_000);
// the milestone a child has reached, and its age in whole days
export function milestoneOf(babies: BabiesContent, bornAt: Date | string, now: Date) {
  const age = Math.max(0, Math.floor((now.getTime() - new Date(bornAt).getTime()) / 86_400_000));
  const sorted = [...babies.milestones].sort((a, b) => a.day - b.day);
  return { age, milestone: [...sorted].reverse().find((item) => age >= item.day) ?? sorted[0]! };
}
// kin is stored once per pair as what the second life is to the first; the first sees it, the second the inverse
export const kinSeenBy = (stored: KinKind | null, viewerIsFirst: boolean): KinKind | null =>
  stored ? (viewerIsFirst ? stored : KIN_INVERSE[stored]) : null;

// ---------- over the wire ----------

export const BabyName = FirstName;
export const CHILD_ORIGINS = ['born', 'adopted'] as const;
export const ChildView = z.object({
  id: z.string().uuid(),
  name: z.string(),
  origin: z.enum(CHILD_ORIGINS),
  // when the baby arrives (or arrived)
  bornAt: z.string(),
  born: z.boolean(),
  // the other parent (you are the first)
  parent: z.object({ lifeId: z.string().uuid(), name: z.string() }),
  age: z.number().int(),
  milestone: BabyMilestone,
  care: z.number().int().min(0).max(100),
  hungry: z.boolean(),
  // when it can be fed or played with next, and renamed (null: now)
  feedFrom: z.string().nullable(),
  playFrom: z.string().nullable(),
  renameFrom: z.string().nullable(),
  // a new name is waiting for the other parent's answer
  renaming: z.boolean(),
});
export type ChildView = z.infer<typeof ChildView>;
export const RelativeView = z.object({
  lifeId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
  // what they are to you (null: family from before kinds existed)
  kin: z.enum(KIN_KINDS).nullable(),
});
export type RelativeView = z.infer<typeof RelativeView>;
export const ChildCareRequest = z.object({ action: z.enum(['feed', 'play']) }).strict();
export type ChildCareRequest = z.infer<typeof ChildCareRequest>;
